/*
 * newsbot - forked from autobrr
 */

import { Fragment, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { PlusIcon, PencilSquareIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";

import { useAuth } from "@contexts/AuthContext";
import { classNames } from "@utils";
import { toast } from "@components/hot-toast";
import Toast from "@components/notifications/Toast";
import { DeleteModal } from "@components/modals";
import { EmptySimple } from "@components/emptystates";
import { Section } from "./_components";
import { api } from "@api/client";

interface UserInterface {
  id: string;
  email: string;
  role: "admin" | "editor";
  display_name: string | null;
}

interface CreateUserInput {
  email: string;
  password: string;
  display_name?: string;
  role: "admin" | "editor";
}

interface UpdateUserInput {
  role: "admin" | "editor";
}

// Query keys
const userKeys = {
  all: ["users"] as const,
  detail: (id: string) => ["users", id] as const,
};

function UsersSettings() {
  const { isAdmin } = useAuth();
  const [addUserOpen, setAddUserOpen] = useState(false);

  const { data: users, isLoading } = useQuery({
    queryKey: userKeys.all,
    queryFn: () => api.get<UserInterface[]>("/auth/users"),
    enabled: isAdmin,
    staleTime: 30_000,
  });

  if (!isAdmin) {
    return (
      <Section title="Users" description="Manage user accounts and roles.">
        <div className="rounded-md bg-yellow-50 dark:bg-yellow-900/20 p-4">
          <p className="text-sm text-yellow-700 dark:text-yellow-400">
            You must be an admin to manage users.
          </p>
        </div>
      </Section>
    );
  }

  if (isLoading) {
    return (
      <Section title="Users" description="Manage user accounts and roles.">
        <div className="animate-pulse">Loading...</div>
      </Section>
    );
  }

  return (
    <Section title="Users" description="Manage user accounts and roles.">
      <div className="mb-4">
        <button
          type="button"
          onClick={() => setAddUserOpen(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
          Add User
        </button>
      </div>

      {users && users.length > 0 ? (
        <ul className="min-w-full relative">
          <li className="grid grid-cols-12 border-b border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wider">
            <div className="col-span-4 pl-4 py-3">User</div>
            <div className="col-span-2 py-3">Role</div>
            <div className="col-span-2 py-3">Actions</div>
          </li>
          {users.map((user) => (
            <UserListItem key={user.id} user={user} />
          ))}
        </ul>
      ) : (
        <EmptySimple title="No users" subtitle="Add a user to get started" />
      )}

      <AddUserDialog isOpen={addUserOpen} onClose={() => setAddUserOpen(false)} />
    </Section>
  );
}

interface UserListItemProps {
  user: UserInterface;
}

function UserListItem({ user }: UserListItemProps) {
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const cancelModalButtonRef = useRef(null);
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/auth/users/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.custom((t) => (
        <Toast type="success" body={`User ${user.email} was deleted`} t={t} />
      ));
    },
    onError: (error: Error) => {
      toast.custom((t) => (
        <Toast type="error" body={`Failed to delete user: ${error.message}`} t={t} />
      ));
    },
  });

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case "admin":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300";
      case "editor":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
    }
  };

  return (
    <>
      <li key={user.id}>
        <div className="grid grid-cols-12 items-center text-sm font-medium text-gray-900 dark:text-gray-500">
          <div className="col-span-4 pl-4 py-3 flex flex-col">
            <span className="dark:text-white truncate">{user.display_name || user.email}</span>
            {user.display_name && (
              <span className="text-xs text-gray-500 truncate">{user.email}</span>
            )}
          </div>
          <div className="col-span-2 py-3">
            <span
              className={classNames(
                getRoleBadgeColor(user.role),
                "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
              )}
            >
              {user.role}
            </span>
          </div>
          <div className="col-span-2 py-3 flex gap-2">
            <button
              type="button"
              onClick={() => setEditUserOpen(true)}
              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
              title="Edit role"
            >
              <PencilSquareIcon className="h-5 w-5" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => setDeleteModalOpen(true)}
              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
              title="Delete user"
            >
              <TrashIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </li>

      <EditUserDialog
        isOpen={editUserOpen}
        onClose={() => setEditUserOpen(false)}
        user={user}
      />

      <DeleteModal
        isOpen={deleteModalOpen}
        isLoading={deleteMutation.isPending}
        toggle={() => setDeleteModalOpen(!deleteModalOpen)}
        buttonRef={cancelModalButtonRef}
        deleteAction={() => {
          deleteMutation.mutate(user.id);
          setDeleteModalOpen(false);
        }}
        title={`Remove user: ${user.email}`}
        text="Are you sure you want to remove this user? This action cannot be undone."
      />
    </>
  );
}

interface AddUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

function AddUserDialog({ isOpen, onClose }: AddUserDialogProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<CreateUserInput>({
    email: "",
    password: "",
    display_name: "",
    role: "editor",
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateUserInput) => api.post<UserInterface>("/auth/users", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.custom((t) => <Toast type="success" body="User created successfully" t={t} />);
      onClose();
      setFormData({ email: "", password: "", display_name: "", role: "editor" });
    },
    onError: (error: Error) => {
      toast.custom((t) => (
        <Toast type="error" body={`Failed to create user: ${error.message}`} t={t} />
      ));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <DialogTitle
                      as="h3"
                      className="text-lg font-semibold leading-6 text-gray-900 dark:text-white"
                    >
                      Add User
                    </DialogTitle>
                    <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                      <div>
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Email
                        </label>
                        <input
                          type="email"
                          id="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="password"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Password
                        </label>
                        <input
                          type="password"
                          id="password"
                          required
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="display_name"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Display Name (optional)
                        </label>
                        <input
                          type="text"
                          id="display_name"
                          value={formData.display_name}
                          onChange={(e) =>
                            setFormData({ ...formData, display_name: e.target.value })
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="role"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Role
                        </label>
                        <select
                          id="role"
                          value={formData.role}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              role: e.target.value as "admin" | "editor",
                            })
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                        >
                          <option value="editor">Editor</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>

                      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                        <button
                          type="submit"
                          disabled={createMutation.isPending}
                          className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {createMutation.isPending ? "Creating..." : "Create User"}
                        </button>
                        <button
                          type="button"
                          onClick={onClose}
                          className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-200 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

interface EditUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserInterface;
}

function EditUserDialog({ isOpen, onClose, user }: EditUserDialogProps) {
  const queryClient = useQueryClient();
  const [role, setRole] = useState<"admin" | "editor">(user.role);

  const updateMutation = useMutation({
    mutationFn: (data: UpdateUserInput) =>
      api.patch<UserInterface>(`/auth/users/${user.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.custom((t) => <Toast type="success" body="User role updated successfully" t={t} />);
      onClose();
    },
    onError: (error: Error) => {
      toast.custom((t) => (
        <Toast type="error" body={`Failed to update user: ${error.message}`} t={t} />
      ));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate({ role });
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <DialogTitle
                      as="h3"
                      className="text-lg font-semibold leading-6 text-gray-900 dark:text-white"
                    >
                      Edit User Role
                    </DialogTitle>
                    <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Email
                        </label>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          {user.email}
                        </p>
                      </div>

                      <div>
                        <label
                          htmlFor="role"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Role
                        </label>
                        <select
                          id="role"
                          value={role}
                          onChange={(e) => setRole(e.target.value as "admin" | "editor")}
                          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                        >
                          <option value="editor">Editor</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>

                      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                        <button
                          type="submit"
                          disabled={updateMutation.isPending}
                          className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {updateMutation.isPending ? "Saving..." : "Save Changes"}
                        </button>
                        <button
                          type="button"
                          onClick={onClose}
                          className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-200 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default UsersSettings;
